import React, { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Button } from '~common/components/button'
import { Icon } from '~common/components/icon/icon'
import { ExpandableSection } from '../section-wrapper'
import type { SettingsModalLogic } from '../../logic'

interface EmailForwardingSectionProps {
    logic: SettingsModalLogic
    expanded: boolean
    onToggle: () => void
    showTitleInSection?: boolean
}

export const EmailForwardingSection: React.FC<EmailForwardingSectionProps> = ({
    logic,
    expanded,
    onToggle,
    showTitleInSection = true,
}) => {
    const { t } = useTranslation('settings')
    const [isCopied, setIsCopied] = useState(false)

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(
                logic.getEmailForwardingAddress(),
            )
            setIsCopied(true)
            setTimeout(() => setIsCopied(false), 2000)
        } catch (err) {
            console.error('Failed to copy email address:', err)
        }
    }

    return (
        <ExpandableSection
            title={t('sections.emailForwarding.title')}
            description={t('sections.emailForwarding.description')}
            expanded={expanded}
            onToggle={onToggle}
            iconName="mail"
            iconColor={({ theme }) => theme.colors.text.primary}
            showTitleInSection={showTitleInSection}
        >
            <EmailSection>
                <EmailText>{logic.getEmailForwardingAddress()}</EmailText>
                <CopyButton
                    onClick={handleCopy}
                    variant="primary"
                    size="medium"
                    label={
                        isCopied
                            ? t('sections.emailForwarding.copied')
                            : t('sections.emailForwarding.copy')
                    }
                />
            </EmailSection>
        </ExpandableSection>
    )
}

const EmailSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[3]};
    padding: ${({ theme }) => theme.spacing[2]} 0;
`

const EmailText = styled.div`
    font-family: monospace;
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    color: ${({ theme }) => theme.colors.text.primary};
    word-break: break-all;
    flex: 1;
`

const CopyButton = styled(Button)`
    white-space: nowrap;
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
`
